import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  CalendarDays, RefreshCw, Save, Play, Trash2, Plus, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Weekday shifts (Monday-Friday)
const WEEKDAY_SHIFTS = [
  { value: 'AM', label: 'AM', start: '09:00', end: '17:00' },
  { value: 'Closing', label: 'Closing', start: '11:30', end: '19:30' }
];

// Saturday shift
const SATURDAY_SHIFT = { value: 'Saturday', label: 'Saturday', start: '08:30', end: '12:30' };

// Helper to get available shifts for a day
const getShiftsForDayIndex = (dayIndex) => {
  if (dayIndex === 5) return [SATURDAY_SHIFT]; // Saturday
  if (dayIndex === 6) return []; // Sunday - closed
  return WEEKDAY_SHIFTS; // Weekday
};

export default function WeeklyTemplateDialog({ open, onOpenChange, onSuccess }) {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('create');
  const [loading, setLoading] = useState(false);
  
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Template creation form
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateDays, setTemplateDays] = useState({
    0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: []
  });

  // Apply template form
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [applyStartDate, setApplyStartDate] = useState('');
  const [applyEndDate, setApplyEndDate] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchEmployees();
      fetchTemplates();
    }
  }, [open]);

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/users?status=active');
      setEmployees(res.data.filter(u => u.role === 'employee'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/schedules/templates?schedule_type=employee');
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleToggleEmployeeShift = (dayIndex, employeeId, shiftBucket) => {
    setTemplateDays(prev => {
      const dayShifts = [...prev[dayIndex]];
      const existingIndex = dayShifts.findIndex(
        s => s.employee_id === employeeId && s.shift_bucket === shiftBucket
      );
      
      if (existingIndex >= 0) {
        dayShifts.splice(existingIndex, 1);
      } else {
        const availableShifts = getShiftsForDayIndex(dayIndex);
        const shift = availableShifts.find(b => b.value === shiftBucket);
        dayShifts.push({
          employee_id: employeeId,
          shift_bucket: shiftBucket,
          start_time: shift?.start || '09:00',
          end_time: shift?.end || '17:00',
          event_type: 'EMPLOYEE_SHIFT'
        });
      }
      
      return { ...prev, [dayIndex]: dayShifts };
    });
  };

  const isEmployeeShiftSelected = (dayIndex, employeeId, shiftBucket) => {
    return templateDays[dayIndex]?.some(
      s => s.employee_id === employeeId && s.shift_bucket === shiftBucket
    );
  };

  const getShiftCount = (dayIndex, shiftBucket) => {
    return templateDays[dayIndex]?.filter(s => s.shift_bucket === shiftBucket).length || 0;
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setLoading(true);
    try {
      await api.post('/schedules/templates', {
        name: templateName,
        description: templateDescription,
        schedule_type: 'employee',
        days: templateDays
      });
      toast.success('Template saved successfully');
      fetchTemplates();
      setTemplateName('');
      setTemplateDescription('');
      setTemplateDays({ 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate || !applyStartDate || !applyEndDate) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/schedules/templates/apply', {
        template_id: selectedTemplate,
        start_date: applyStartDate,
        end_date: applyEndDate,
        overwrite_existing: overwriteExisting
      });
      toast.success(`Created ${res.data.created} schedule entries`);
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} due to conflicts`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/schedules/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-teal-600" />
            Weekly Schedule Template
          </DialogTitle>
          <DialogDescription>
            Create reusable weekly schedule patterns and apply them to date ranges
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="create">Create Template</TabsTrigger>
            <TabsTrigger value="apply">Apply Template</TabsTrigger>
            <TabsTrigger value="manage">Manage Templates</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 pr-4 mt-4">
            {/* Create Template Tab */}
            <TabsContent value="create" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name *</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Standard Week Pattern"
                    data-testid="template-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="Optional description"
                  />
                </div>
              </div>

              {/* Weekly Grid - Weekdays */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-2 text-left font-medium sticky left-0 bg-slate-50 z-10">Employee</th>
                        {WEEKDAYS.slice(0, 5).map((day, idx) => (
                          <th key={day} className="p-2 text-center font-medium min-w-[100px]">
                            <div>{day.slice(0, 3)}</div>
                            <div className="text-xs text-slate-400">AM | Close</div>
                          </th>
                        ))}
                        <th className="p-2 text-center font-medium min-w-[80px] bg-violet-50">
                          <div>Sat</div>
                          <div className="text-xs text-slate-400">8:30-12:30</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => (
                        <tr key={emp.id} className="border-t">
                          <td className="p-2 sticky left-0 bg-white z-10">
                            {emp.first_name} {emp.last_name?.charAt(0)}.
                          </td>
                          {/* Weekday columns (Mon-Fri) */}
                          {[0, 1, 2, 3, 4].map(dayIdx => (
                            <td key={dayIdx} className="p-1 text-center">
                              <div className="flex gap-1 justify-center">
                                {WEEKDAY_SHIFTS.map(shift => (
                                  <Checkbox
                                    key={shift.value}
                                    checked={isEmployeeShiftSelected(dayIdx, emp.id, shift.value)}
                                    onCheckedChange={() => handleToggleEmployeeShift(dayIdx, emp.id, shift.value)}
                                    className="data-[state=checked]:bg-teal-600"
                                    title={`${shift.label} shift (${shift.start}-${shift.end})`}
                                  />
                                ))}
                              </div>
                            </td>
                          ))}
                          {/* Saturday column */}
                          <td className="p-1 text-center bg-violet-50/50">
                            <Checkbox
                              checked={isEmployeeShiftSelected(5, emp.id, SATURDAY_SHIFT.value)}
                              onCheckedChange={() => handleToggleEmployeeShift(5, emp.id, SATURDAY_SHIFT.value)}
                              className="data-[state=checked]:bg-violet-600"
                              title={`Saturday shift (${SATURDAY_SHIFT.start}-${SATURDAY_SHIFT.end})`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td className="p-2 font-medium">Daily Totals</td>
                        {[0, 1, 2, 3, 4].map(dayIdx => (
                          <td key={dayIdx} className="p-2 text-center text-xs">
                            {WEEKDAY_SHIFTS.map(shift => (
                              <Badge 
                                key={shift.value}
                                variant="outline" 
                                className="mx-0.5"
                                title={shift.label}
                              >
                                {shift.value[0]}: {getShiftCount(dayIdx, shift.value)}
                              </Badge>
                            ))}
                          </td>
                        ))}
                        <td className="p-2 text-center text-xs bg-violet-50">
                          <Badge variant="outline">
                            Sat: {getShiftCount(5, SATURDAY_SHIFT.value)}
                          </Badge>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                AM Shift: 9:00-17:00 | Closing Shift: 11:30-19:30 | Saturday: 8:30-12:30
              </p>
            </TabsContent>

            {/* Apply Template Tab */}
            <TabsContent value="apply" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label>Select Template</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="apply-template-select">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date (Monday recommended)</Label>
                  <Input
                    type="date"
                    value={applyStartDate}
                    onChange={(e) => setApplyStartDate(e.target.value)}
                    data-testid="apply-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={applyEndDate}
                    onChange={(e) => setApplyEndDate(e.target.value)}
                    data-testid="apply-end-date"
                  />
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> Start on a Monday for best results. The template will repeat weekly 
                  throughout the selected date range.
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Overwrite Existing</p>
                  <p className="text-xs text-slate-500">Replace schedules if dates are already occupied</p>
                </div>
                <Switch 
                  checked={overwriteExisting}
                  onCheckedChange={setOverwriteExisting}
                />
              </div>
            </TabsContent>

            {/* Manage Templates Tab */}
            <TabsContent value="manage" className="space-y-4 mt-0">
              {templates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No templates created yet</p>
                  <p className="text-sm">Create your first template in the "Create Template" tab</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map(template => (
                    <div 
                      key={template.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                    >
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description && (
                          <p className="text-sm text-slate-500">{template.description}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          Created: {new Date(template.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          
          {activeTab === 'create' && (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleSaveTemplate}
              disabled={loading || !templateName.trim()}
              data-testid="save-template-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Template
            </Button>
          )}
          
          {activeTab === 'apply' && (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleApplyTemplate}
              disabled={loading || !selectedTemplate || !applyStartDate || !applyEndDate}
              data-testid="apply-template-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Apply Template
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
